import { linkifyHtml } from '../utils/linkify';
import DOMPurify from 'dompurify';

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { AdminControls } from '../components/AdminControls';
import { useToast } from '../hooks/useToast';
import type { PageContent, PromptData, PromptTemplate } from '../types';
import promptsData from '../services/data/prompts.json';

export const AiPromptIndexPage: React.FC = () => {
  const { content: allContent, isAdminMode, updatePageContent } = useContent();
  const [pageContent, setPageContent] = useState<PageContent>(allContent.aiPromptGeneratorIndex);
  
  // Use imported data directly
  const promptData: PromptData = promptsData as unknown as PromptData;
  
  const { showToast } = useToast();
  
  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);
  
  useSeo(pageContent, 'aiPromptGeneratorIndex', onSeoApplied);

  useEffect(() => {
    setPageContent(allContent.aiPromptGeneratorIndex);
  }, [allContent.aiPromptGeneratorIndex]);

  const handleSave = async () => {
    const oldSlug = allContent.aiPromptGeneratorIndex?.seo?.slug;
    const redirectCreated = await updatePageContent('aiPromptGeneratorIndex', pageContent);
    showToast('AI Prompt Generator index page saved!');
    if (redirectCreated) {
      const newSlug = pageContent.seo.slug;
      showToast(`Redirect created: ${oldSlug} -> ${newSlug}`);
    }
  };
  
  const handleCopySamples = (e: React.MouseEvent, categoryKey: string) => {
    e.preventDefault(); // Prevent navigation when clicking the button
    e.stopPropagation();

    if (!promptData || !promptData[categoryKey]) return;
    
    const templates = [...promptData[categoryKey].templates];
    const sampleCount = Math.max(1, Math.min(templates.length, Math.floor(Math.random() * 3) + 3)); // 3 to 5 samples
    
    // Fisher-Yates shuffle
    for (let i = templates.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [templates[i], templates[j]] = [templates[j], templates[i]];
    }
    
    const samples = templates.slice(0, sampleCount).join('\n\n---\n\n');
    const fullText = `Sample prompts for ${promptData[categoryKey].name}:\n\n${samples}`;
    
    navigator.clipboard.writeText(fullText).then(() => {
      showToast('Sample prompts copied!');
    }).catch(err => {
      console.error('Failed to copy sample prompts: ', err);
      showToast('Failed to copy samples.');
    });
  };

  const sanitizedBody = pageContent?.body ? linkifyHtml(pageContent.body) : '';

  return (
    <div className="w-full max-w-6xl mx-auto py-8 md:py-12 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
      <div className="w-full max-w-4xl mx-auto">
        <header className="text-center mb-4">
          <h1 className="text-3xl md:text-4xl font-bold text-[#4F46E5]">
            {pageContent.h1}
          </h1>
        </header>
        <div
          className="text-[#6B7280] prose max-w-none mx-auto mb-10"
          dangerouslySetInnerHTML={{ __html: sanitizedBody }}
        />

        {isAdminMode && (
          <AdminControls
            pageId="aiPromptGeneratorIndex"
            pageContent={pageContent}
            onContentChange={setPageContent}
            onSave={handleSave}
          />
        )}

        <div className="mt-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {promptData && Object.entries(promptData).map(([key, prompt]: [string, PromptTemplate]) => (
                <Link 
                  key={prompt.path}
                  to={prompt.path} 
                  className="flex flex-col justify-between p-6 bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div>
                    <h3 className="font-bold text-lg text-[#1F2937]">{prompt.name}</h3>
                    <p className="text-sm text-[#6B7280] mt-2">{prompt.description}</p>
                  </div>
                   <button 
                    onClick={(e) => handleCopySamples(e, key)}
                    className="mt-4 w-full text-center px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-md transition-colors duration-200"
                    aria-label={`Copy sample prompts for ${prompt.name}`}
                  >
                    Copy Sample Prompts
                  </button>
                </Link>
              ))}
            </div>
        </div>
      </div>
    </div>
  );
};
